import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AppState } from 'src/app/store/app.state';

import {
  addProfessorSubjectItem,
  updateProfessorSubjectItem,
} from 'src/app/store/actions/professor.action';
import { setLoadingSpinner } from 'src/app/store/actions/shared.action';

@Component({
  selector: 'app-add-grades',
  templateUrl: './add-grades.component.html',
  styleUrls: ['./add-grades.component.scss'],
})
export class AddGradesComponent implements OnInit {
  data!: any;

  gradesForm: FormGroup;
  student: any;

  termPeriods: Array<{ id: number; period_name: string }> = [];
  components: Array<{ id: number; component_name: string }> = [];
  categories: Array<{ id: number; category_name: string }> = [];

  public onClose: Subject<any> = new Subject();

  constructor(
    private store: Store<AppState>,
    public bsModalRef: BsModalRef,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();

    this.termPeriods = this.data.term_periods;

    for (let gradeComponent of this.data.grade_components) {
      this.components.push({
        id: gradeComponent.id,
        component_name: gradeComponent.component_name,
      });
    }

    if (this.data.selected.grade_code) {
      this.gradesForm.patchValue({
        grade_code: this.data.selected.grade_code,
      });
    }

    if (this.data.selected.grade_value) {
      this.gradesForm.patchValue({
        grade_value: this.data.selected.grade_value,
      });
    }

    if (this.data.selected.description) {
      this.gradesForm.patchValue({
        description: this.data.selected.description,
      });
    }

    this.gradesForm.get('period_id')?.setValue(this.data?.selected?.period);
    this.gradesForm
      .get('component_id')
      ?.setValue(this.data?.selected?.component);
    this.onGradeComponentChange({ value: this.data?.selected?.component });
  }

  initForm(): void {
    this.gradesForm = this.fb.group({
      period_id: [{ value: '', disabled: true }, Validators.required],
      component_id: [{ value: '', disabled: true }, Validators.required],
      category_id: [{ value: '', disabled: true }, Validators.required],
      grade_code: [{ value: '', disabled: true }, Validators.required],
      description: ['', [Validators.required, Validators.maxLength(30)]], // Added description field with validation
      grade_value: [
        '',
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          Validators.pattern(/^\d+$/),
        ],
      ],
    });
  }

  get descriptionControl() {
    return this.gradesForm.get('description');
  }

  onGradeComponentChange(event: any): void {
    const selectedComponent = this.data.grade_components.find(
      (gc) => gc.id == event.value
    );

    if (selectedComponent) {
      this.categories = [];

      for (let category of selectedComponent.categories) {
        this.categories.push({
          id: category.id,
          category_name: category.category_name,
        });
      }

      this.gradesForm.patchValue({
        category_id: this.data?.selected?.category,
      });
    }
  }

  async save(): Promise<void> {
    this.markFormGroupTouched(this.gradesForm);

    let payload = this.gradesForm.value;

    if (this.gradesForm.valid) {
      if (this.data.selected.id) {
        payload = {
          ...payload,
          ...{ grade_code: this.data?.selected?.grade_code },
        };

        this.store.dispatch(
          updateProfessorSubjectItem({
            payload,
            subjectId: this.data.subjectId,
            id: this.data.selected.id,
          })
        );
      } else {
        payload = {
          ...payload,
          ...{
            period_id: this.data?.selected?.period,
            component_id: this.data?.selected?.component,
            category_id: this.data?.selected?.category,
            grade_code: this.data?.selected?.grade_code,
          },
        };

        this.store.dispatch(
          addProfessorSubjectItem({ payload, subjectId: this.data.subjectId })
        );
      }

      this.onClose.next({ action: 'save', data: payload });
      this.bsModalRef.hide();
    }
  }

  noChanges(): boolean {
    return (
      this.gradesForm.get('grade_code')?.value ===
        this.data?.selected?.grade_code &&
      this.gradesForm.get('grade_value')?.value ===
        this.data?.selected?.grade_value &&
      this.gradesForm.get('description')?.value ===
        this.data?.selected?.description
    );
  }

  clear(): void {
    this.gradesForm.get('description')?.reset();
    this.gradesForm.get('grade_value')?.reset();
  }

  close(): void {
    this.onClose.next({ action: 'cancel' });
    this.bsModalRef.hide();
  }

  onGradeInput(event: any) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');

    let value = input.value;
    if (parseInt(value) > 100) input.value = '100';
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }
}
